/* eslint-disable jsx-a11y/anchor-is-valid */
import type { FC } from "react";
import { useState, useEffect, useMemo } from "react";
import {
  PageLayout,
  InstantSearch,
  TableSkeleton,
  StandardTable,
} from "@components";
import { useWorkspace } from "../../context/WorkspaceContext";
import { useAdminDashboard } from "../../hooks";
import { useTranslation } from "react-i18next";
import { ITableRow, IWorkspaceAccess, WorkspaceRole } from "../../types";
import {
  getCustomerAccountDetails,
  getCustomerAccountName,
} from "./workspacePresentation";

const displayDate = (value: string | null | undefined, locale: string) => {
  if (!value) return null;
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
};

export const WorkspacePage: FC = function () {
  const [isLoading, setLoading] = useState(true);
  const [tableRows, setTableRows] = useState([] as ITableRow[]);
  const [searchTerm, setSearchTerm] = useState("");
  const { t, i18n } = useTranslation(["common", "user", "workspace"]);
  const locale = i18n.language === "no" ? "nb-NO" : "en-GB";
  const { activeWorkspace, setActiveWorkspace } = useWorkspace();
  const query = useAdminDashboard(30);

  const isQueryLoading = useMemo(
    () => query.isLoading || query.isRefetching,
    [query],
  );

  const queryData = useMemo(() => query.data?.workspaces ?? [], [query.data]);

  useEffect(() => {
    if (!isQueryLoading) {
      setLoading(false);

      // A workspace is the technical tenancy boundary. Admins identify the
      // customer account primarily by the companies that belong to it.
      let filteredWorkspaces = queryData.filter((workspace) => {
        const normalizedSearch = searchTerm.toLocaleLowerCase();
        return [
          workspace.name,
          workspace.owner,
          workspace.id,
          ...workspace.companyNames,
        ].some((value) =>
          value?.toLocaleLowerCase().includes(normalizedSearch),
        );
      });

      setTableRows(
        filteredWorkspaces.map((workspace) => {
          return {
            id: workspace.id,
            isActive: workspace.id === activeWorkspace?.workspaceId,
            cells: [
              {
                id: "name",
                children: (
                  <div className="min-w-0">
                    <div className="truncate font-medium text-text">
                      {getCustomerAccountName(workspace)}
                    </div>
                    <div className="truncate text-xs text-secondary">
                      {getCustomerAccountDetails(workspace)}
                    </div>
                  </div>
                ),
                showOnSmallScreen: true,
              },
              {
                id: "owner",
                children: workspace.owner,
              },
              {
                id: "companyCount",
                children: workspace.companyCount,
              },
              {
                id: "ordersInPeriod",
                children: workspace.ordersInPeriod,
              },
              {
                id: "lastActivityAt",
                children:
                  displayDate(workspace.lastActivityAt, locale) ||
                  t("workspace:workspaces.no_activity"),
              },
              {
                id: "createdAt",
                children: displayDate(workspace.createdAt, locale) || "—",
              },
            ],
            onRowClickData: workspace,
          };
        }),
      );
    }
  }, [isQueryLoading, queryData, t, activeWorkspace, searchTerm, locale]);

  return (
    <PageLayout>
      <div className="flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <div className="pb-4">
              <h1 className="text-xl font-semibold text-text sm:text-2xl">
                {t("workspace:admin_title")}
              </h1>
              <p className="text-secondary text-base pt-2 pb-4 dark:text-secondary">
                {t("workspace:admin_description")}
              </p>

              {/* Search Controls */}
              <div className="mb-4">
                <div className="w-full sm:w-1/2 min-w-0">
                  <InstantSearch
                    placeholder={t("workspace:workspaces.search_placeholder")}
                    value={searchTerm}
                    onChange={setSearchTerm}
                  />
                </div>
              </div>
            </div>
            <div className="overflow-hidden shadow">
              {isLoading ? (
                <TableSkeleton />
              ) : (
                <StandardTable
                  header={[
                    {
                      id: "name",
                      children: t("workspace:workspaces.customer_account"),
                      showOnSmallScreen: true,
                    },
                    {
                      id: "owner",
                      children: t("workspace:workspaces.owner"),
                    },
                    {
                      id: "companyCount",
                      children: t("workspace:workspaces.companies"),
                    },
                    {
                      id: "ordersInPeriod",
                      children: t("workspace:workspaces.orders_30d"),
                    },
                    {
                      id: "lastActivityAt",
                      children: t("workspace:workspaces.last_activity"),
                    },
                    {
                      id: "createdAt",
                      children: t("common:created_at"),
                    },
                  ]}
                  rows={tableRows}
                  canMultiSelect={false}
                  onRowClick={(workspace) => {
                    const workspaceAccess: IWorkspaceAccess = {
                      workspaceId: workspace.id,
                      workspaceName: workspace.name,
                      name: workspace.owner,
                      email: workspace.owner,
                      role: WorkspaceRole.Owner,
                      isApiKey: false,
                      createdBy: workspace.owner,
                    };
                    setActiveWorkspace(workspaceAccess);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
